import { api } from "../api/client";
import { useAsync } from "../lib/useAsync";
import { SkeletonList } from "../components/Skeleton";

function time(iso: string): string {
  return new Date(iso).toLocaleString(undefined, {
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
    second: "2-digit",
  });
}

const ACTION_ICON: Record<string, string> = {
  rsvp: "✅",
  join_team: "➕",
  create_team: "🏗️",
  create_meet: "📅",
  cancel_meet: "🚫",
  admin_assign_team: "🛠️",
  admin_remove_team: "🧹",
};

export function AdminActivity() {
  const { data, loading, error } = useAsync(() => api.adminActivity(24), []);

  return (
    <div>
      <h1>User activity</h1>
      <p className="muted">
        Authenticated user actions over the last 24 hours, grouped by user and
        login session. Events auto-expire after 24 hours.
        {data ? ` ${data.totalEvents} event(s).` : ""}
      </p>

      {loading && <SkeletonList count={4} variant="card" />}
      {error && <p className="error">{error}</p>}

      <div className="stagger">
        {data?.users.map((u) => (
          <div key={u.userId} className="card">
            <div className="row spread">
              <strong>
                {u.displayName ?? u.email ?? `User ${u.userId.slice(0, 8)}`}
              </strong>
              <span className="row" style={{ gap: "0.35rem" }}>
                <span className="badge state">
                  {u.sessionCount} session{u.sessionCount === 1 ? "" : "s"}
                </span>
                <span className="badge accent">{u.eventCount} events</span>
              </span>
            </div>

            {u.sessions.map((s) => (
              <div
                key={s.sessionId}
                style={{
                  marginTop: "0.6rem",
                  paddingLeft: "0.75rem",
                  borderLeft: "2px solid var(--hairline)",
                }}
              >
                <div className="muted" style={{ fontSize: "0.8rem" }}>
                  Session{" "}
                  <code>{s.sessionId.slice(0, 8)}</code> · {s.count} event
                  {s.count === 1 ? "" : "s"} · {time(s.firstAt)} → {time(s.lastAt)}
                </div>
                <div className="grid" style={{ marginTop: "0.3rem" }}>
                  {s.events.map((e) => (
                    <div
                      key={e.eventId}
                      className="row"
                      style={{ gap: "0.5rem", fontSize: "0.9rem" }}
                    >
                      <span aria-hidden="true">
                        {ACTION_ICON[e.action] ?? "•"}
                      </span>
                      <span className="muted" style={{ minWidth: 150 }}>
                        {time(e.createdAt)}
                      </span>
                      <span>{e.summary}</span>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        ))}
      </div>

      {data && data.users.length === 0 && (
        <p className="muted">No activity recorded in the last 24 hours.</p>
      )}
    </div>
  );
}
